import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Global courier loader: every API call reports pending state; CourierLoader shows the
// animated overlay only when a request actually takes time. Pass skipGlobalLoader on a
// request config to opt out, or loaderLabel to override the themed message.
let apiPending = 0;
const apiLabels = new Map();
const apiCouriers = new Map();
const ROUTE_LABELS = [
  [/\/shipments\/book/, "Booking your shipment…"],
  [/\/rates\//, "Fetching live rates…"],
  [/\/admin\/couriers\/sync/, "Syncing couriers…"],
  [/\/serviceability|\/pincode\//, "Checking serviceability…"],
  [/\/auth\/login/, "Signing you in…"],
  [/\/track(\/|$)/, "Fetching live tracking…"],
  [/\/shipments\/.*\/document/, "Preparing your document…"],
  [/\/shipments\/.*\/cancel/, "Cancelling shipment…"],
  [/\/drafts/, "Saving your draft…", "post"],
  [/\/drafts/, "Loading drafts…"],
  [/\/warehouses/, "Saving warehouse…", "post|put"],
  [/\/warehouses/, "Loading warehouses…"],
  [/\/shipments/, "Loading shipments…"],
  [/\/wallet/, "Loading wallet…"],
  [/\/manifests/, "Loading manifests…"],
  [/\/couriers/, "Loading operators…"],
  [/\/kyc/, "Checking KYC…"],
  [/\/support/, "Working on it…"],
];
const routeLabel = (cfg) => {
  if (cfg?.loaderLabel) return cfg.loaderLabel;
  const url = cfg?.url || "";
  const method = cfg?.method || "get";
  for (const [re, label, m] of ROUTE_LABELS) {
    if (re.test(url) && (!m || new RegExp(`^(${m})$`).test(method))) return label;
  }
  return null;
};
const lastLabel = () => {
  const vals = [...apiLabels.values()];
  return vals[vals.length - 1] || null;
};
const lastCourier = () => {
  const vals = [...apiCouriers.values()];
  return vals[vals.length - 1] || null;
};
const notifyApiPending = () => window.dispatchEvent(new CustomEvent("shipany:api-pending", { detail: { pending: apiPending, label: lastLabel(), courier: lastCourier() } }));
const apiStart = (cfg) => {
  if (!cfg?.skipGlobalLoader) {
    apiPending += 1;
    const label = routeLabel(cfg);
    if (label) apiLabels.set(cfg, label);
    if (cfg.loaderCourier) apiCouriers.set(cfg, cfg.loaderCourier);
    notifyApiPending();
  }
};
const apiStop = (cfg) => {
  if (!cfg?.skipGlobalLoader) {
    apiPending = Math.max(0, apiPending - 1);
    apiLabels.delete(cfg);
    apiCouriers.delete(cfg);
    notifyApiPending();
  }
};

let refreshing = null;
api.interceptors.request.use((c) => { apiStart(c); return c; }, (e) => Promise.reject(e));
api.interceptors.response.use(
  (r) => { apiStop(r.config); return r; },
  async (error) => {
    apiStop(error.config);
    const orig = error.config || {};
    if (error.response?.status === 401 && !orig._retried && !String(orig.url).includes("/auth/")) {
      orig._retried = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
      try {
        await refreshing;
        return api(orig);
      } catch (e) { /* fall through */ }
    }
    return Promise.reject(error);
  }
);

export function fmtErr(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && x.msg) || JSON.stringify(x)).join(" ");
  return String(d);
}

export const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) + ", " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

export default api;
